import { useTranslation } from 'react-i18next';
import { Link, Outlet, type ErrorComponentProps } from '@tanstack/react-router';
import { Compass, RefreshCw, TriangleAlert } from 'lucide-react';
import { StepUpDialog } from '@features/auth/components/StepUpDialog';
import { describeError, Button, Card, EmptyState, LogoMark, Skeleton } from '@shared/ui';

/** App root: skip link, live regions, global dialogs. Toaster/Tooltip providers live in AppProviders. */
// z-debug is not in the Tailwind z-index scale (it compiled to nothing), so the focused link sat under the sidebar.
export function RootLayout() {
  const { t } = useTranslation('common');
  return (
    <>
      <a
        href="#main"
        className="sr-only rounded-md bg-primary text-on-primary shadow-3 focus:not-sr-only focus:fixed focus:start-4 focus:top-4 focus:z-offline focus:px-4 focus:py-2 focus:focus-ring"
      >
        {t('a11y.skipToMain')}
      </a>
      <Outlet />
      <StepUpDialog />
      {/* One polite status region and one alert region per page (a11y doc §14). */}
      <div id="status" aria-live="polite" className="sr-only" />
      <div id="alerts" role="alert" className="sr-only" />
    </>
  );
}

export function PendingPage() {
  const { t } = useTranslation('common');
  return (
    <div role="status" aria-label={t('status.loading')} className="flex flex-col gap-4 py-6">
      <Skeleton className="h-8 w-1/3" />
      <Skeleton lines={4} />
    </div>
  );
}

export function NotFoundPage() {
  const { t } = useTranslation('common');
  return (
    <div className="mx-auto flex min-h-[60dvh] max-w-lg flex-col items-center justify-center px-page-x">
      <LogoMark size={40} />
      <EmptyState
        icon={Compass}
        title={t('errors.notFound.title')}
        description={t('errors.notFound.body')}
        action={
          <Link to="/" className="link font-medium">
            {t('errors.notFound.home')}
          </Link>
        }
      />
    </div>
  );
}

export function RouteErrorPage({ error, reset }: ErrorComponentProps) {
  const { t } = useTranslation('common');
  const { title, detail, requestId } = describeError(error, t);
  return (
    <Card className="mx-auto my-8 max-w-lg">
      <EmptyState
        icon={TriangleAlert}
        title={t('errors.unexpected.title')}
        description={
          <>
            <p>{title}</p>
            {detail ? <p className="mt-1">{detail}</p> : null}
            {requestId ? (
              <p className="mt-2 text-xs">
                {t('errors.requestId')} <bdi className="ltr-island font-mono">{requestId}</bdi>
              </p>
            ) : null}
          </>
        }
        action={
          <Button variant="secondary" leadingIcon={RefreshCw} onClick={reset}>
            {t('actions.retry')}
          </Button>
        }
      />
    </Card>
  );
}
